import * as React from "react";
import { useNavigate } from "react-router-dom";

interface Video {
    src: string;
    title: string;
}

interface FloatingVideoWindowProps {
    videos: Video[];
    interval?: number; // milliseconds between video changes
}

const FloatingVideoWindow: React.FC<FloatingVideoWindowProps> = ({ 
    videos, 
    interval = 8000 // 8 seconds default
}) => {
    const navigate = useNavigate();
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [isPlaying, setIsPlaying] = React.useState(true);
    const videoRef = React.useRef<HTMLVideoElement>(null);

    // Auto-advance to next video
    React.useEffect(() => {
        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % videos.length);
        }, interval);

        return () => clearInterval(timer);
    }, [videos.length, interval]);

    // Reset video when index changes
    React.useEffect(() => {
        if (videoRef.current) {
            videoRef.current.load();
            if (isPlaying) {
                videoRef.current.play().catch(() => {
                    // Autoplay might be blocked, that's okay
                    setIsPlaying(false);
                });
            }
        }
    }, [currentIndex]);

    const handleClick = () => {
        navigate(`/videos?v=${currentIndex}`);
    };

    const handleMouseEnter = () => {
        if (videoRef.current) {
            videoRef.current.play().catch(() => {});
        }
    };

    const handleMouseLeave = () => {
        if (videoRef.current) {
            videoRef.current.pause();
        }
    };

    return (
        <div
            className="fixed top-20 right-4 z-40 cursor-pointer group"
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
                animation: "slideInFromRight 0.5s ease-out",
            }}
        >
            {/* Container */}
            <div className="relative w-32 md:w-40 lg:w-48 rounded-xl overflow-hidden shadow-2xl border-4 transition-all duration-300 transform group-hover:scale-105 group-hover:shadow-3xl"
                style={{ 
                    borderColor: "#DB1C7D",
                    boxShadow: "0 10px 40px rgba(219, 28, 125, 0.4)"
                }}
            >
                {/* Video */}
                <div className="relative aspect-[9/16] bg-black">
                    <video
                        ref={videoRef}
                        className="w-full h-full object-cover"
                        muted
                        loop
                        playsInline
                        preload="metadata"
                    >
                        <source src={videos[currentIndex].src} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>

                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {/* Play icon overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center transform group-hover:scale-110 transition-transform">
                            <svg className="w-8 h-8 ml-1" style={{ color: "#DB1C7D" }} fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </div>
                    </div>

                    {/* Title overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="text-white text-xs font-semibold truncate">
                            {videos[currentIndex].title}
                        </p>
                    </div>
                </div>

                {/* Progress indicators */}
                <div className="absolute top-2 left-2 right-2 flex gap-1">
                    {videos.map((_, index) => (
                        <div
                            key={index}
                            className="flex-1 h-1 rounded-full overflow-hidden"
                            style={{ backgroundColor: "rgba(255, 255, 255, 0.3)" }}
                        >
                            <div
                                className="h-full transition-all duration-300"
                                style={{
                                    width: index === currentIndex ? "100%" : index < currentIndex ? "100%" : "0%",
                                    backgroundColor: "#DB1C7D"
                                }}
                            />
                        </div>
                    ))}
                </div>

                {/* Click hint badge */}
                <div className="absolute top-2 right-2 bg-pink-500 text-white text-xs px-2 py-1 rounded-full font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Watch Full
                </div>

                {/* Video counter */}
                <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full font-medium">
                    {currentIndex + 1}/{videos.length}
                </div>
            </div>

            {/* Pulse animation ring */}
            <div className="absolute inset-0 rounded-xl pointer-events-none">
                <div
                    className="absolute inset-0 rounded-xl animate-ping opacity-20"
                    style={{ 
                        borderWidth: "2px",
                        borderColor: "#DB1C7D",
                        animationDuration: "3s"
                    }}
                />
            </div>
        </div>
    );
};

export default FloatingVideoWindow;
